import { useRef, useEffect } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

/**
 * Cloudflare Turnstile widget — anti-bot CAPTCHA on signup + guest flows.
 *
 * Renders nothing when `REACT_APP_TURNSTILE_SITE_KEY` is unset (the
 * dev default), so local devs / pytest / preview builds keep working
 * without a Cloudflare account. Once the env var is set, the widget
 * mounts, completes a silent challenge in ~200ms, and emits a token
 * via `onToken(token)`. The token is then attached to the next
 * register/guest request as an `X-Turnstile-Token` header.
 *
 * v5.7.52 — paired with the v5.7.51 per-IP daily caps to defend
 * against bot-farms that rotate IPs (which IP rate-limits can't catch
 * but Turnstile's cryptographic + behavioural signals can).
 */
export default function TurnstileWidget({ onToken, onError, theme = 'auto', size = 'flexible', resetKey = 0 }) {
  const widgetRef = useRef(null);
  const siteKey = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();

  // Reset the widget when the site key changes (dev hot-reload) OR when the
  // parent bumps `resetKey` after a rejected submit — Turnstile tokens are
  // SINGLE-USE and expire (~300s), so a reused/stale token yields a backend
  // "timeout-or-duplicate" rejection. Re-running the challenge hands the user
  // a fresh token automatically instead of dead-ending on "refresh the page".
  useEffect(() => {
    if (widgetRef.current && typeof widgetRef.current.reset === 'function') {
      try { widgetRef.current.reset(); } catch { /* noop */ }
    }
  }, [siteKey, resetKey]);

  if (!siteKey) {
    // Disabled — render nothing. Backend mirrors this with a graceful
    // skip when TURNSTILE_SECRET_KEY is unset.
    return null;
  }

  return (
    <div
      className="flex justify-center my-2"
      data-testid="turnstile-widget"
    >
      <Turnstile
        ref={widgetRef}
        siteKey={siteKey}
        onSuccess={(token) => onToken && onToken(token)}
        onError={(err) => {
          onToken && onToken(null);
          onError && onError(err);
          // Self-heal: fetch a fresh challenge so the user isn't stranded.
          try { widgetRef.current?.reset?.(); } catch { /* noop */ }
        }}
        onExpire={() => {
          onToken && onToken(null);
          try { widgetRef.current?.reset?.(); } catch { /* noop */ }
        }}
        options={{ theme, size, appearance: 'always', refreshExpired: 'auto', retry: 'auto' }}
      />
    </div>
  );
}
